import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FiAlertCircle, FiEye, FiSearch, FiShield } from "react-icons/fi";
import useAuth from "../../../hooks/useAuth";
import { repoUserService } from "../../../services/repoUser.service";
import DataTableToolbar from "../../../components/common/DataTableToolbar";
import ViewBlockReasonModal from "../../../components/common/ViewBlockReasonModal";
import PaginationBar from "../../../components/common/PaginationBar";
import { PAGE_LIMIT } from "../../../constants/pagination";
import { readListItems, readPagination } from "../../../utils/listPagination";
import { saveExcelBlob, getApiErrorMessage } from "../../../utils/downloadExcel";
import { printTablePage } from "../../../utils/printTable";
import { formatRepoRole } from "../../../constants/repoRoles";
import { loadRazorpayScript, openRazorpayCheckout } from "../../../utils/razorpayCheckout";
import "../../../styles/users.css";

function formatBlockedDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return "—";
  }
}

export default function Blacklist() {
  const { auth } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [pendingIds, setPendingIds] = useState(() => new Set());
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);
  const [reasonViewTarget, setReasonViewTarget] = useState(null);
  const [billing, setBilling] = useState(null);
  const isFirstLoad = useRef(true);
  const isRepoAdmin = auth?.user?.role === "REPO_ADMIN";

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(searchQuery), 350);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    setPage(1);
  }, [debouncedQuery]);

  const loadBilling = useCallback(async () => {
    if (!auth?.token || !isRepoAdmin) return;
    try {
      const res = await repoUserService.getConnectEligibility(auth.token);
      setBilling(res?.data || res || null);
    } catch {
      setBilling(null);
    }
  }, [auth?.token, isRepoAdmin]);

  const loadData = useCallback(
    async ({ silent = false } = {}) => {
      const showFullLoader = !silent && isFirstLoad.current;
      if (!auth?.token) {
        if (showFullLoader) setLoading(false);
        return;
      }
      try {
        if (showFullLoader) setLoading(true);
        if (!silent) setError("");
        const params = {
          blocked: true,
          page,
          limit: PAGE_LIMIT,
        };
        if (debouncedQuery.trim()) params.q = debouncedQuery.trim();
        const res = await repoUserService.getUsers(auth.token, params);
        setUsers(readListItems(res));
        setPagination(readPagination(res));
      } catch (err) {
        if (!silent) {
          setError(err?.response?.data?.message || "Failed to load blocked users");
          setUsers([]);
        }
      } finally {
        if (showFullLoader) {
          setLoading(false);
          isFirstLoad.current = false;
        }
      }
    },
    [auth?.token, debouncedQuery, page]
  );

  useEffect(() => {
    loadData({ silent: !isFirstLoad.current });
  }, [loadData]);

  useEffect(() => {
    loadBilling();
  }, [loadBilling]);

  const payAndActivateUser = async (user) => {
    const userId = user._id || user.id;
    await loadRazorpayScript();
    const orderRes = await repoUserService.createUserActivationOrder(userId, auth.token);
    const data = orderRes?.data || {};
    const checkout = await openRazorpayCheckout({
      key: data.razorpayKeyId,
      orderId: data.orderId,
      amount: data.amount,
      currency: data.currency,
      description: "User activation fee",
      prefill: {
        name: auth?.user?.name || "",
        email: auth?.user?.email || "",
        contact: auth?.user?.phone || "",
      },
    });
    await repoUserService.verifyUserActivationPayment(
      userId,
      {
        razorpay_order_id: checkout.razorpay_order_id,
        razorpay_payment_id: checkout.razorpay_payment_id,
        razorpay_signature: checkout.razorpay_signature,
        paymentId: data.paymentId,
      },
      auth.token
    );
  };

  const handleUnblock = async (user, { fromModal = false } = {}) => {
    const userId = user._id || user.id;
    setPendingIds((prev) => new Set(prev).add(userId));
    setUsers((prev) => prev.filter((row) => String(row._id || row.id) !== String(userId)));
    try {
      try {
        await repoUserService.changeUserStatus(userId, true, auth.token);
      } catch (err) {
        const needsPay =
          err?.response?.status === 402 ||
          err?.response?.data?.requiresPayment ||
          err?.response?.data?.code === "CONNECT_FEE_REQUIRED";
        if (!needsPay) throw err;
        await payAndActivateUser(user);
      }
      if (fromModal) setReasonViewTarget(null);
      await Promise.all([loadData({ silent: true }), loadBilling()]);
    } catch (err) {
      await loadData({ silent: true });
      if (err?.message === "CHECKOUT_CLOSED") {
        alert("Payment was cancelled. User was not unblocked.");
      } else {
        alert(err?.response?.data?.message || "Failed to unblock user");
      }
    } finally {
      setPendingIds((prev) => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
    }
  };

  const handleExportExcel = async () => {
    if (!auth?.token) return;
    try {
      setExporting(true);
      const response = await repoUserService.downloadUsersExcel(auth.token, {
        blocked: true,
        q: debouncedQuery.trim() || undefined,
      });
      await saveExcelBlob(response, "blocked-users.xlsx");
    } catch (err) {
      alert(await getApiErrorMessage(err, "Failed to download Excel file"));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="page companies-page blacklist-page">
      <div className="users-actions">
        <h2>Blacklist</h2>
      </div>
      <p className="muted" style={{ marginTop: 0, marginBottom: 16 }}>
        Users blocked from your agency. They cannot sign in until you unblock them.
      </p>

      <div className="company-search-panel company-search-panel--simple">
        <div className="company-search-bar">
          <FiSearch className="company-search-icon" aria-hidden />
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name, email, phone, or role..."
            aria-label="Search blocked users"
          />
        </div>
      </div>

      {error ? <p className="error-text">{error}</p> : null}

      <DataTableToolbar
        onDownloadExcel={handleExportExcel}
        onPrint={printTablePage}
        downloading={exporting}
        downloadLabel="Download blocked users (Excel)"
      />

      {loading && users.length === 0 ? (
        <p>Loading blacklist...</p>
      ) : (
        <div className="company-table-wrap printable-table-area">
          <h3 className="print-only-title">Blocked users</h3>
          <table className="users-table excel-grid-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Role</th>
                <th>Status</th>
                <th>Blocked on</th>
                <th>Reason</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan="8">No blocked users.</td>
                </tr>
              ) : (
                users.map((user) => {
                  const userId = user._id || user.id;
                  const isPending = pendingIds.has(userId);
                  return (
                    <tr key={userId} className={isPending ? "row-pending" : ""}>
                      <td>{user.name || "—"}</td>
                      <td>{user.email || "—"}</td>
                      <td>{user.phone || "—"}</td>
                      <td>{formatRepoRole(user.role)}</td>
                      <td>
                        <span className="company-status company-status--inactive">Blocked</span>
                      </td>
                      <td>{formatBlockedDate(user.blockedAt)}</td>
                      <td>{user.blockReason || "—"}</td>
                      <td className="company-actions-td">
                        <div className="company-actions-cell company-actions-cell--blacklist">
                          <Link
                            to={`/users/${userId}`}
                            className="company-btn company-btn--view"
                          >
                            <FiEye aria-hidden />
                            <span>View</span>
                          </Link>
                          <button
                            type="button"
                            className="company-btn company-btn--reason"
                            disabled={isPending}
                            onClick={() => setReasonViewTarget(user)}
                          >
                            <FiAlertCircle aria-hidden />
                            <span>Reason</span>
                          </button>
                          {isRepoAdmin ? (
                            <button
                              type="button"
                              className="company-btn company-btn--unblock"
                              disabled={isPending}
                              onClick={() => handleUnblock(user)}
                            >
                              <FiShield aria-hidden />
                              <span>
                                {billing?.requiresActivationFee
                                  ? `Unblock (₹${Number(
                                      billing.activationFeeAmount || 100
                                    ).toLocaleString("en-IN")})`
                                  : "Unblock"}
                              </span>
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      <PaginationBar
        page={pagination.page || page}
        totalPages={pagination.totalPages}
        total={pagination.total}
        loading={loading}
        onPageChange={setPage}
      />

      <ViewBlockReasonModal
        open={Boolean(reasonViewTarget)}
        companyName={reasonViewTarget?.name}
        blockReason={reasonViewTarget?.blockReason}
        blockedAt={reasonViewTarget?.blockedAt}
        onClose={() => setReasonViewTarget(null)}
        onUnblock={
          isRepoAdmin && reasonViewTarget
            ? () => handleUnblock(reasonViewTarget, { fromModal: true })
            : undefined
        }
        unblocking={
          reasonViewTarget
            ? pendingIds.has(reasonViewTarget._id || reasonViewTarget.id)
            : false
        }
      />
    </div>
  );
}
